import React, { useState } from 'react';
import { db, collection, addDoc, handleFirestoreError, OperationType, Timestamp, createNotification, query, where, getDocs } from '../firebase';
import { Task, UserProfile } from '../types';
import { useAuth } from '../App';
import { Star, X, MessageSquare, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface Props {
  task: Task;
  isOpen: boolean;
  onClose: () => void;
}

const FeedbackModal: React.FC<Props> = ({ task, isOpen, onClose }) => {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { profile } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await addDoc(collection(db, 'feedback'), {
        taskId: task.id,
        clientId: task.clientId,
        clientName: task.clientName || 'Unknown Client',
        rating,
        comment,
        createdAt: Timestamp.now()
      });

      // Notify managers
      const qManagers = query(collection(db, 'users'), where('role', 'in', ['admin', 'manager']));
      const managersSnapshot = await getDocs(qManagers);
      const managers = managersSnapshot.docs.map(doc => doc.data() as UserProfile);

      for (const manager of managers) {
        await createNotification(
          manager.uid,
          'New Client Feedback',
          `New ${rating}-star feedback received for task: ${task.title}`,
          'feedback_received',
          '/feedback'
        );
      }

      onClose();
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center text-amber-600">
                  <Star className="w-6 h-6 fill-current" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Client Feedback</h2>
                  <p className="text-xs text-slate-500">Rate your experience for this task</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="mb-6 p-4 bg-blue-50/50 rounded-2xl border border-blue-100">
              <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Task</p>
              <p className="text-sm font-black text-blue-900 tracking-tight">{task.title}</p>
              <p className="text-xs text-blue-600 font-medium mt-1">{task.clientName}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-3 text-center">
                <label className="text-sm font-black text-slate-700 tracking-tight">How would you rate the service?</label>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      className="p-1 transition-transform hover:scale-110 active:scale-95"
                    >
                      <Star
                        className={`w-10 h-10 ${
                          star <= rating ? 'fill-orange-500 text-orange-500' : 'text-slate-200'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-xs font-black text-orange-600 uppercase tracking-widest">
                  {rating === 5 ? 'Excellent' : rating === 4 ? 'Very Good' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-black text-slate-700 tracking-tight">Comments (Optional)</label>
                <div className="relative">
                  <MessageSquare className="absolute left-4 top-4 text-slate-400 w-5 h-5" />
                  <textarea
                    rows={4}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all resize-none font-medium"
                    placeholder="Tell us what you think..."
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-blue-800 text-white px-6 py-4 rounded-2xl font-black hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 uppercase tracking-widest"
              >
                {isSubmitting ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Feedback
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default FeedbackModal;
